import type { UserRole } from './types'

/**
 * THE TWO SIDES OF THE APPLICATION.
 *
 * OVAL buys two entirely different things and the board wanted them kept
 * apart:
 *
 *   MATERIALS  cement, steel, sand. A purchase order, a lorry, a store-man
 *              counting bags, stock, issues, returns, transfers.
 *   SERVICE    work. A works contract, an engineer with a tape, a measurement
 *              book, a running account bill. Nothing arrives and nothing is
 *              stored.
 *
 * They share a login, a vendor master and a payables ledger, and share nothing
 * else. So the application is one deployment with two front doors, and this
 * file is the only place that knows both exist.
 *
 * NO REACT IN HERE. It is policy, and the tests import it directly - the same
 * reason nav.ts is a plain module. Importing a component into the test runner
 * drags in react-router and the '@/' alias and falls over on the alias, which
 * is a good sign the test is reaching into the wrong place.
 */

export type ModuleKey = 'materials' | 'service' | 'finance'

export interface ModuleDef {
  key: ModuleKey
  /** What it is called on the card and in the switcher. */
  title: string
  /** One line, on the card. Says what you would come here to DO. */
  blurb: string
  /** Where clicking the card lands you. */
  home: string
  /** Every route on this side begins with this. '' means the root. */
  prefix: string
  /** Who may open it at all. */
  roles: UserRole[]
}

/**
 * WHO MAY OPEN THE SERVICE SIDE — decided 14-09-2026.
 *
 * Everyone who has anything to do with a works contract, and nobody else:
 *
 *   site_engineer              measures the work
 *   site_construction_manager  certifies the measurement
 *   project_manager            counter-certifies the bill, owns the site
 *   procurement_manager        writes the work order
 *   procurement_head           owns the commercial terms
 *   project_head               programme and cost
 *   cmd                        signs the contract
 *   cxo                        reads the numbers
 *   accounts_spoc              pays, and owes the reverse-charge GST
 *   admin                      maintains the system
 *
 * DELIBERATELY EXCLUDED, and this is the point of the list:
 *
 *   storeman         keeps a store. A works contract never touches his stock,
 *                    he cannot measure it and he cannot approve it. Putting
 *                    the card in front of him is a door he would try once,
 *                    find empty, and mistrust thereafter.
 *   store_executive  the same, at Head Office.
 *
 * They are not blocked by hiding a card - every Service table's row level
 * security refuses them too. The card is the courtesy; the policy is the
 * control.
 */
export const SERVICE_ROLES: UserRole[] = [
  'site_engineer',
  'site_construction_manager',
  'project_manager',
  'procurement_manager',
  'procurement_head',
  'project_head',
  'cmd',
  'cxo',
  'accounts_spoc',
  'admin',
]

/**
 * Everybody sees Materials. That has been true since go-live and nothing here
 * changes it - a store-man who signs in must land exactly where he landed
 * yesterday.
 */
export const MATERIAL_ROLES: UserRole[] = [
  'admin', 'cxo', 'cmd', 'project_head', 'project_manager',
  'procurement_head', 'procurement_manager', 'accounts_spoc',
  'stores_head', 'store_executive', 'site_construction_manager', 'storeman',
  'site_engineer',
]

/**
 * WHO MAY OPEN PROJECT FINANCE — decided 01-10-2026 (migrations 0189, 0190).
 *
 * The CMD's module: payment requisitions, their approval and release, and each
 * project's profit or loss against its contract value. Money people and the
 * people who raise and approve requisitions, and nobody else:
 *
 *   project_manager      raises requisitions for his own sites
 *   procurement_manager  raises them on a Project Manager's behalf
 *   procurement_head     approves
 *   project_head         approves
 *   cmd                  final signature
 *   cfo                  final signature in the CMD's absence
 *   cxo                  reads the numbers
 *   accounts_spoc        releases the payments and keeps the proof
 *   admin                maintains the system
 *
 * No site role, no stores role. The database refuses them on every pf_ table
 * as well; this list is the courtesy, the row-level security is the control.
 */
export const FINANCE_ROLES: UserRole[] = [
  'project_manager',
  'procurement_manager',
  'procurement_head',
  'project_head',
  'cmd',
  'cfo',
  'cxo',
  'accounts_spoc',
  'admin',
]

export const MODULES: ModuleDef[] = [
  {
    key: 'materials',
    title: 'Material Procurement',
    blurb: 'Indents, requisitions, purchase orders, goods receipts, stock, issues and transfers.',
    home: '/',
    prefix: '',
    roles: MATERIAL_ROLES,
  },
  {
    key: 'service',
    title: 'Service Procurement',
    blurb: 'Works contracts, the Measurement Book, running account bills and contractor payments.',
    home: '/service',
    prefix: '/service',
    roles: SERVICE_ROLES,
  },
  {
    key: 'finance',
    title: 'Project Finance',
    blurb: 'Payment requisitions, approvals and releases, and the profit or loss of every project.',
    home: '/finance',
    prefix: '/finance',
    roles: FINANCE_ROLES,
  },
]

export const moduleFor = (key: ModuleKey): ModuleDef =>
  MODULES.find((m) => m.key === key) ?? MODULES[0]

export function mayUseModule(role: UserRole | null | undefined, key: ModuleKey): boolean {
  if (!role) return false
  return moduleFor(key).roles.includes(role)
}

/** The sides this person may open, in card order. */
export function modulesFor(role: UserRole | null | undefined): ModuleDef[] {
  if (!role) return []
  return MODULES.filter((m) => m.roles.includes(role))
}

/**
 * Which side does a path belong to?
 *
 * Longest prefix wins, so '/service/work-orders' is Service and '/services'
 * would not be - the leading slash and the exact segment both matter.
 */
export function moduleOfPath(pathname: string): ModuleKey {
  if (pathname === '/finance' || pathname.startsWith('/finance/')) return 'finance'
  return pathname === '/service' || pathname.startsWith('/service/')
    ? 'service'
    : 'materials'
}

// ---------------------------------------------------------------------------
// REMEMBERING THE CHOICE
//
// The board asked for the two cards; the people using it every day asked not
// to meet them every morning. So the chooser appears once and the answer is
// remembered, with a switcher in the header for the handful of people who
// genuinely work on both sides.
//
// localStorage, not the database: it is a convenience about THIS browser, not
// a fact about the person. Somebody at a site terminal and the same person on
// his phone can reasonably land in different places, and neither is wrong.
//
// Wrapped in try/catch throughout. Private browsing, a full disk and some
// managed Android builds all make localStorage throw rather than return null,
// and a thrown preference must never stop somebody signing in.
// ---------------------------------------------------------------------------
const REMEMBER_KEY = 'oval.module'

export function rememberedModule(): ModuleKey | null {
  try {
    const v = localStorage.getItem(REMEMBER_KEY)
    return v === 'materials' || v === 'service' || v === 'finance' ? v : null
  } catch {
    return null
  }
}

export function rememberModule(key: ModuleKey): void {
  try {
    localStorage.setItem(REMEMBER_KEY, key)
  } catch {
    /* nothing to do, and nothing worth telling the user about */
  }
}

export function forgetModule(): void {
  try {
    localStorage.removeItem(REMEMBER_KEY)
  } catch { /* as above */ }
}

/**
 * Where should this person go when they sign in?
 *
 *   entitled to one side only  -> straight there, no chooser, ever
 *   entitled to both, remembered -> where they were last
 *   entitled to both, first time -> the chooser
 *
 * A store-man is in the first case and will never see the two cards, which is
 * correct: a screen offering one real choice and one locked door is worse than
 * no screen.
 */
export function landingFor(role: UserRole | null | undefined): string {
  const mine = modulesFor(role)
  if (mine.length === 0) return '/login'
  if (mine.length === 1) return mine[0].home

  const last = rememberedModule()
  if (last && mine.some((m) => m.key === last)) return moduleFor(last).home
  return '/choose'
}
